import { css } from '@emotion/react'
import { colorTokens } from '../../styles/tokens'

/**
 * ClaimRoutingCard styles.
 *
 * The card is a decorative hero illustration that replaces a screenshot, so it
 * is built to *scale like an image*: the root establishes an inline-size query
 * container and every dimension is expressed in `cqw`, meaning the whole card
 * shrinks proportionally with its width (capped by the wrapper `max-width`).
 * `u(px)` converts a design-reference pixel value (at width `DW`) into `cqw`.
 *
 * The sage surfaces from the source mockup have no equivalent in the shared
 * token palette — the same gap the stat-cards `mint` tone documents — so they
 * live here as local constants. Greens map onto the brand tokens.
 */
const panelBg = '#f3f5ef'
const ringTrack = '#dde7dc'
const barTrack = '#e7eae2'
const pillBg = '#e0ecdf'
const hairline = '#edefe9'
const eyebrowGray = '#9aa39a'
const mutedSage = '#8a908a'

const DW = 560
const u = (px: number) => `${((px / DW) * 100).toFixed(3)}cqw`

// Cabinet Grotesk 900 display treatment — matches the home hero and stat cards.
const headingFont = css`
  font-family: var(--font-cabinet-grotesk), 'system-ui', Arial, sans-serif;
  font-weight: 900;
`

export const styles = {
  root: css`
    container-type: inline-size;
    width: 100%;
    max-width: ${DW}px;
    margin-inline: auto;
  `,
  card: css`
    box-sizing: border-box;
    width: 100%;
    background: ${colorTokens.white};
    border-radius: ${u(28)};
    padding: ${u(40)} ${u(44)} ${u(32)};
    box-shadow: 0 24px 56px -34px rgba(20, 30, 20, 0.24);
    font-family: 'system-ui', Arial, sans-serif;
    color: ${colorTokens.green900};

    /* Desktop only: the 3D tilt + directional shadow the hero image used to carry. */
    @media (min-width: 768px) {
      box-shadow: -24px 32px 56px -24px rgba(20, 39, 27, 0.32);
      transform: perspective(1600px) rotateY(-14deg) rotateX(5deg) rotate(1deg);
      transform-origin: center;
    }
  `,
  content: css`
    will-change: opacity, transform;
  `,

  header: css`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: ${u(16)};
  `,
  eyebrow: css`
    font-size: ${u(15)};
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${eyebrowGray};
  `,
  title: [
    headingFont,
    css`
      font-size: ${u(34)};
      line-height: 1.05;
      letter-spacing: -0.01em;
      margin-top: ${u(8)};
      color: ${colorTokens.green900};
    `,
  ],
  pill: css`
    flex: none;
    background: ${pillBg};
    color: ${colorTokens.green200};
    font-size: ${u(17)};
    font-weight: 600;
    padding: ${u(11)} ${u(22)};
    border-radius: 999px;
    white-space: nowrap;
  `,

  panel: css`
    margin-top: ${u(28)};
    background: ${panelBg};
    border-radius: ${u(22)};
    padding: ${u(30)} ${u(34)};
    display: flex;
    align-items: center;
    gap: ${u(30)};
  `,
  ringWrap: css`
    position: relative;
    flex: none;
    width: ${u(150)};
    height: ${u(150)};
  `,
  ringSvg: css`
    display: block;
    width: 100%;
    height: 100%;
  `,
  ringCenter: css`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  `,
  score: [
    headingFont,
    css`
      font-size: ${u(46)};
      line-height: 1;
      color: ${colorTokens.green900};
    `,
  ],
  scoreLabel: css`
    font-size: ${u(13)};
    font-weight: 600;
    letter-spacing: 0.14em;
    color: ${eyebrowGray};
    margin-top: ${u(4)};
  `,
  panelTitle: css`
    font-size: ${u(24)};
    font-weight: 700;
    color: ${colorTokens.green900};
  `,
  panelBody: css`
    font-size: ${u(18)};
    font-weight: 400;
    line-height: 1.45;
    color: ${mutedSage};
    margin-top: ${u(8)};
  `,

  partnersLabel: css`
    margin-top: ${u(32)};
    font-size: ${u(15)};
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${eyebrowGray};
  `,
  rows: css`
    margin-top: ${u(20)};
    display: flex;
    flex-direction: column;
    gap: ${u(22)};
  `,
  row: css`
    display: flex;
    align-items: center;
    gap: ${u(20)};
    height: ${u(56)};
    will-change: transform;
    transition: transform 0.6s cubic-bezier(0.65, 0, 0.35, 1);
  `,
  avatar: css`
    flex: none;
    width: ${u(56)};
    height: ${u(56)};
    border-radius: ${u(14)};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${u(17)};
    font-weight: 700;
  `,
  rowMain: css`
    flex: 1;
    min-width: 0;
  `,
  rowTop: css`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: ${u(12)};
  `,
  name: css`
    font-size: ${u(22)};
    font-weight: 600;
    color: ${colorTokens.green900};
  `,
  pct: css`
    font-size: ${u(20)};
    font-weight: 700;
  `,
  barTrack: css`
    margin-top: ${u(12)};
    height: ${u(9)};
    border-radius: 999px;
    background: ${barTrack};
    overflow: hidden;
  `,
  barFill: css`
    height: 100%;
    width: 0;
    border-radius: 999px;
  `,

  footer: css`
    margin-top: ${u(30)};
    border-top: 1px solid ${hairline};
    padding-top: ${u(22)};
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${u(16)};
    font-size: ${u(18)};
  `,
  footerMeta: css`
    color: ${eyebrowGray};
    font-weight: 400;
  `,
  footerAction: css`
    font-weight: 600;
    color: ${colorTokens.green200};
    white-space: nowrap;
  `,

  ringTrack: css`
    stroke: ${ringTrack};
  `,
}
